import { memo, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Container, Stack, Typography } from "@mui/material";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import WorkspacePremiumOutlined from "@mui/icons-material/WorkspacePremiumOutlined";
import { useNavigate } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import { usePlan } from "../../Hooks/usePlan";

/**
 * /premium/welcome — Stripe's success_url after checkout. The subscription
 * webhook usually lands a second after the redirect, so this polls GET /api/me
 * briefly until the plan reads premium, then confirms what unlocked. If the
 * webhook is slow it never dead-ends: it shows a "being set up" note instead.
 */
const UNLOCKED = [
  "Maintenance reminders built for your home",
  "Your full Home History — every repair, improvement and permit",
  "Documents — deeds, insurance, tax bills in one place",
  "Unlimited home photos",
  "Your complete record, ready to hand over when you sell",
];

const POLL_MS = 2000;
const MAX_POLLS = 8; // ~16s before we fall back to the "being set up" note

const PremiumWelcome = memo(() => {
  const navigate = useNavigate();
  const { isAuthenticated, plan, refetch } = usePlan();
  const [attempts, setAttempts] = useState(0);

  const isPremium = plan?.tier === "premium";
  const stillWaiting = !isPremium && attempts < MAX_POLLS;

  // Not signed in → nothing to confirm; send them to the Premium page.
  useEffect(() => {
    if (!isAuthenticated) navigate("/premium", { replace: true });
  }, [isAuthenticated, navigate]);

  // Poll /api/me until premium shows (the webhook lands) or we give up.
  useEffect(() => {
    if (isPremium || attempts >= MAX_POLLS) return;
    const t = setTimeout(() => {
      refetch();
      setAttempts((a) => a + 1);
    }, POLL_MS);
    return () => clearTimeout(t);
  }, [isPremium, attempts, refetch]);

  return (
    <Container maxWidth="sm">
      <FullPaddingWrapper>
        <Stack spacing={3} alignItems="center" textAlign="center" sx={{ py: { xs: 6, md: 10 } }}>
          {stillWaiting ? (
            <>
              <CircularProgress sx={{ color: "primary.main" }} />
              <Typography variant="h5" fontWeight={700} color="primary.main">
                Finishing up your Premium…
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Thanks for subscribing. We're just confirming your payment — this only takes a moment.
              </Typography>
            </>
          ) : isPremium ? (
            <>
              <CheckCircleRounded sx={{ color: "success.main", fontSize: 72 }} />
              <Typography variant="h4" fontWeight={700} color="primary.main" data-testid="welcome-premium-on">
                You're Premium! 🎉
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Everything's unlocked. Here's what you can do now:
              </Typography>
              <Stack component="ul" spacing={1.25} sx={{ listStyle: "none", p: 0, m: 0, textAlign: "left" }}>
                {UNLOCKED.map((item) => (
                  <Stack component="li" key={item} direction="row" spacing={1} alignItems="flex-start">
                    <CheckRounded sx={{ color: "primary.main", fontSize: 20, mt: "2px" }} />
                    <Typography variant="body1">{item}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Button
                variant="contained"
                startIcon={<WorkspacePremiumOutlined />}
                onClick={() => navigate("/")}
                sx={{ textTransform: "none", borderRadius: 2, px: 4, fontWeight: 700 }}
                data-testid="welcome-go-home"
              >
                Go to my home
              </Button>
            </>
          ) : (
            <>
              <CheckCircleRounded sx={{ color: "success.main", fontSize: 72 }} />
              <Typography variant="h5" fontWeight={700} color="primary.main" data-testid="welcome-pending">
                Payment received — setting up your Premium
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your subscription is going through. It can take a minute to show up — refresh this page shortly,
                or head to your home and it'll be unlocked there.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setAttempts(0);
                    refetch();
                  }}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Check again
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate("/")}
                  sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
                >
                  Go to my home
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </FullPaddingWrapper>
    </Container>
  );
});

export default PremiumWelcome;
